import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { ThumbsUp, MessageSquare, Share2, MapPin, Clock, User, CheckCircle, AlertCircle, Users, TrendingUp, Heart } from 'lucide-react';
import { formatDate, shareContent } from '../utils/helpers';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IssueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [issue, setIssue] = useState(null);
  const [leader, setLeader] = useState(null);
  const [supportingLeaders, setSupportingLeaders] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [hasVoted, setHasVoted] = useState(false);
  const [voting, setVoting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIssue();
    fetchComments();
    checkVoteStatus();
  }, [id]);

  const fetchIssue = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/issues/${id}`);
      setIssue(response.data);
      
      // Fetch leader info
      const leaderId = response.data.primary_leader_id || response.data.politician_id;
      if (leaderId) {
        try {
          const leaderRes = await axios.get(`${API_URL}/api/leaders/${leaderId}`);
          setLeader(leaderRes.data);
        } catch {
          // Try politicians endpoint as fallback
          try {
            const polRes = await axios.get(`${API_URL}/api/politicians/${leaderId}`);
            setLeader({
              ...polRes.data,
              leader_id: polRes.data.politician_id,
              photo_url: polRes.data.profile_image,
              role: 'MLA'
            });
          } catch {
            console.error('Could not fetch leader info');
          }
        }
      }
      
      // Fetch supporting leaders
      const supportingIds = response.data.supporting_leader_ids || [];
      if (supportingIds.length > 0) {
        const supportingLeadersData = [];
        for (const sId of supportingIds.slice(0, 3)) { // Limit to 3
          try {
            const sLeaderRes = await axios.get(`${API_URL}/api/leaders/${sId}`);
            supportingLeadersData.push(sLeaderRes.data);
          } catch {
            // Skip if not found
          }
        }
        setSupportingLeaders(supportingLeadersData);
      }
    } catch (error) {
      console.error('Error fetching issue:', error);
      toast({
        title: 'Error',
        description: 'Failed to load issue',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/comments/${id}`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const checkVoteStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/issues/${id}/vote-status`, { withCredentials: true });
      setHasVoted(response.data.has_voted);
    } catch (error) {
      // API may not exist yet, ignore
      console.log('Vote status check not available');
    }
  };

  const handleVote = async () => {
    if (voting) return;
    setVoting(true);
    try {
      const response = await axios.post(`${API_URL}/api/issues/${id}/vote`, {}, { withCredentials: true });
      setHasVoted(response.data.action === 'voted');
      fetchIssue();
      toast({
        title: response.data.action === 'voted' ? '✋ Counted!' : 'Vote Removed',
        description: response.data.action === 'voted' 
          ? 'You\'ve been counted among citizens affected by this issue' 
          : 'Your vote has been removed',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to vote',
        variant: 'destructive',
      });
    } finally {
      setVoting(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const formData = new FormData();
      formData.append('target_id', id);
      formData.append('text', newComment);

      await axios.post(`${API_URL}/api/comments`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewComment('');
      fetchComments();
      toast({
        title: 'Success',
        description: 'Comment added',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add comment',
        variant: 'destructive',
      });
    }
  };

  const handleShare = (platform) => {
    // Use /api/share/issue/{id} for proper Open Graph tags
    const shareUrl = `${window.location.origin}/api/share/issue/${id}`;
    const result = shareContent(platform, issue.title, issue.description, shareUrl);
    if (result === 'copied') {
      toast({
        title: 'Success',
        description: 'Link copied to clipboard',
      });
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved':
        return 'bg-green-100 text-green-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Action Scheduled':
        return 'bg-purple-100 text-purple-700';
      case 'Rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Road': '🛣️',
      'Electricity': '⚡',
      'Water': '💧',
      'Healthcare': '🏥',
      'Education': '📚',
      'Public Safety': '🛡️',
      'Corruption': '⚠️',
      'Government Scheme': '🏛️',
      'Sanitation': '🧹',
      'Environment': '🌳',
      'Transport': '🚌',
      'Housing': '🏠',
    };
    return icons[category] || '📋';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Issue not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Issue Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {issue.image_url && (
                <div className="bg-gray-100 flex items-center justify-center">
                  <img
                    src={getImageUrl(issue.image_url)}
                    alt={issue.title}
                    loading="lazy"
                    className="w-full max-h-[400px] object-contain"
                  />
                </div>
              )}
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    {/* Category & Status */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {issue.category && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                          {getCategoryIcon(issue.category)} {issue.category}
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(issue.status)}`}>
                        {issue.status}
                      </span>
                    </div>
                    
                    <h1 className="text-2xl font-bold text-gray-900 mb-3">{issue.title}</h1>
                    
                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {issue.constituency}, {issue.state}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDate(issue.timestamp)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {issue.user_name || 'Anonymous'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-base text-gray-700 leading-relaxed mb-6">{issue.description}</p>

                {/* Leader Response (if any) - ABOVE COMMENTS */}
                {issue.leader_response && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-5 mb-6 relative">
                    {/* Official Badge */}
                    <div className="absolute -top-3 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-sm">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Official Response
                    </div>
                    
                    <div className="flex gap-4 mt-2">
                      {/* Leader Photo */}
                      {leader && (
                        <div 
                          className="flex-shrink-0 cursor-pointer"
                          onClick={() => navigate(`/leaders/${leader.leader_id || leader.politician_id}`)}
                        >
                          <img
                            src={getImageUrl(leader.photo_url || leader.profile_image)}
                            alt={leader.name}
                            className="w-14 h-14 rounded-full object-cover border-2 border-green-200 shadow-sm"
                            onError={(e) => { e.target.src = '/logo.png'; }}
                          />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        {/* Leader Info */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-green-800">
                            {issue.leader_response_by_name || leader?.name || 'Leader'}
                          </span>
                          {leader && (
                            <span className="text-xs bg-green-200 text-green-700 px-2 py-0.5 rounded-full">
                              {leader.role || 'MLA'}
                            </span>
                          )}
                        </div>
                        
                        {/* Response Text */}
                        <p className="text-green-900 leading-relaxed">{issue.leader_response}</p>
                        
                        {/* Timestamp */}
                        {issue.leader_response_at && (
                          <p className="text-xs text-green-600 mt-3 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Responded on {formatDate(issue.leader_response_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-3 pt-4 border-t">
                  <Button
                    data-testid="vote-issue-btn"
                    onClick={handleVote}
                    disabled={voting}
                    variant={hasVoted ? 'default' : 'outline'}
                    className={`${hasVoted 
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md' 
                      : 'hover:bg-blue-50 border-blue-200'} transition-all min-w-[180px]`}
                  >
                    {voting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Heart className={`mr-2 h-4 w-4 ${hasVoted ? 'fill-current' : ''}`} />
                        {hasVoted ? '✓ I face this too' : 'I face this issue'}
                        <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                          {issue.vote_count}
                        </span>
                      </>
                    )}
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button data-testid="share-issue-btn" variant="outline">
                        <Share2 className="mr-2 h-4 w-4" />
                        Share
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => handleShare('whatsapp')}>WhatsApp</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleShare('twitter')}>Twitter</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleShare('facebook')}>Facebook</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleShare('linkedin')}>LinkedIn</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleShare('telegram')}>Telegram</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleShare('copy')}>Copy Link</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <span className="flex items-center gap-1 text-gray-600 text-sm">
                    <MessageSquare className="h-4 w-4" />
                    {comments.length} Comments
                  </span>
                </div>
              </div>
            </div>

            {/* Comments Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                Comments ({comments.length})
              </h2>

              {user && (
                <form onSubmit={handleComment} className="mb-6">
                  <Textarea
                    data-testid="comment-input"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts or additional information..."
                    rows={3}
                    className="mb-3"
                  />
                  <Button
                    type="submit"
                    data-testid="submit-comment-btn"
                    className="bg-blue-600 text-white hover:bg-blue-700 rounded-full"
                  >
                    Add Comment
                  </Button>
                </form>
              )}

              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.comment_id} className="border-b pb-4 last:border-b-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-medium">
                        {(comment.user_name || 'A')[0].toUpperCase()}
                      </div>
                      <span className="font-medium text-gray-900">{comment.user_name || 'Anonymous'}</span>
                      <span className="text-sm text-gray-500">{formatDate(comment.timestamp)}</span>
                    </div>
                    <p className="text-gray-700 pl-10">{comment.text}</p>
                  </div>
                ))}
                {comments.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No comments yet. Be the first to comment!</p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Responsible Leader Card */}
            {leader && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-blue-600" />
                  Responsible Leader
                </h3>
                <div
                  onClick={() => navigate(`/leaders/${leader.leader_id || leader.politician_id}`)}
                  className="cursor-pointer hover:bg-gray-50 p-4 rounded-lg transition-colors -mx-2"
                  data-testid="leader-card"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={getImageUrl(leader.photo_url || leader.profile_image)}
                      alt={leader.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
                      onError={(e) => { e.target.src = '/logo.png'; }}
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{leader.name}</h4>
                      <p className="text-sm text-gray-600">{leader.role || 'MLA'}</p>
                      <p className="text-sm text-gray-500">{leader.party}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t">
                    <span className="text-xl font-bold text-blue-600">{(leader.avg_rating || 0).toFixed(1)}</span>
                    <span className="text-yellow-400 text-lg">★</span>
                    <span className="text-sm text-gray-500">({leader.rating_count || 0} ratings)</span>
                  </div>
                </div>
              </div>
            )}

            {/* Supporting Leaders */}
            {supportingLeaders.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  Supporting Leaders
                </h3>
                <div className="space-y-3">
                  {supportingLeaders.map((sLeader) => (
                    <div
                      key={sLeader.leader_id}
                      onClick={() => navigate(`/leaders/${sLeader.leader_id}`)}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <img
                        src={getImageUrl(sLeader.photo_url)}
                        alt={sLeader.name}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                        onError={(e) => { e.target.src = '/logo.png'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{sLeader.name}</h4>
                        <p className="text-xs text-gray-500">{sLeader.role} • {sLeader.party}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-bold text-blue-600">{(sLeader.avg_rating || 0).toFixed(1)}</span>
                        <span className="text-yellow-400 text-sm">★</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No Leader Assigned */}
            {!leader && (
              <div className="bg-yellow-50 rounded-xl shadow-sm border border-yellow-200 p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Leader Assigned</h3>
                <p className="text-sm text-yellow-700">
                  This issue has not been assigned to any leader yet. It may be under review.
                </p>
              </div>
            )}

            {/* Issue Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Issue Impact
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Citizens Affected</span>
                  <span className="text-xl font-bold text-blue-600">{issue.vote_count}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Comments</span>
                  <span className="text-xl font-bold text-gray-900">{comments.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(issue.status)}`}>
                    {issue.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Share Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Spread the Word</h3>
              <p className="text-sm text-gray-500 mb-4">
                Help this issue get noticed by sharing it with others
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => handleShare('whatsapp')} className="flex-1">
                  WhatsApp
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleShare('twitter')} className="flex-1">
                  Twitter
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleShare('copy')} className="flex-1">
                  Copy Link
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssueDetail;
